import { LatLng, Membership, UserPermissionKey } from 'shared/types';
import { CSSProperties } from 'react';
import {
  mapboxTopoStyle,
  mapboxSatelliteStyle,
  mapboxStreetStyle,
} from './styles/mapboxStyles';
import { GeoEventTypes, RouterEntityDrawer } from '../../constants';

// Types defined here because of circular dependency
export type MapLayer =
  | (typeof MapLayers)[keyof typeof MapLayers]
  | ExternalFeatureLayerName;

/**
 * How a layer is gated. Holding every entry in `requiredPermissions` grants
 * access on its own, but those permissions are release-flag gated on the
 * backend, so a layer that uses them must also declare the `minMembership` we
 * fall back to (which is also the paywall shown on denial). Without a tier
 * there'd be nothing to fall back to and no paywall to offer (hence
 * `requiredPermissions` makes `minMembership` required).
 */
type MapLayerAccessGate =
  | MembershipOnlyGate
  | PermissionsWithMembershipFallbackGate;

type MembershipOnlyGate = {
  minMembership?: Membership;
  requiredPermissions?: undefined;
};

type PermissionsWithMembershipFallbackGate = {
  minMembership: Membership;
  requiredPermissions: UserPermissionKey[];
};

export type MapLayerDetail = (
  | { titleKey: string; subtitleKey: string }
  | { title: string; subtitle: string }
) &
  MapLayerAccessGate & {
    requiresWebGL2: boolean;
    imageSrc?: string;
    internalOnly?: boolean;
    experimental?: boolean;
    keyboardShortcut?: string;
    // Adds a timeline play button to this layer's layers drawer item
    timelineLayerId?: TimelineLayerId;
  };

export type ExternalFeatureLayerName = string & {
  __brand: 'ExternalFeatureLayer';
};

export type DefaultViewState = {
  latitude: number;
  longitude: number;
  zoom: number;
  minZoom: number;
  maxZoom: number;
  style: CSSProperties;
};

export const SELECTED_FEATURE_HIGHLIGHT_COLOR = '#52E8EB';

export const GEO_EVENT_MARKER_Z_INDEX = 9;

const activeTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const DEFAULT_HAWAII: LatLng = { lat: 20.7148175, lng: -156.6114244 };
const DEFAULT_ALASKA: LatLng = { lat: 64.941287, lng: -151.508081 };

const TIME_ZONE_TO_DEFAULT_LATLNG: Record<string, LatLng> = {
  'Pacific/Honolulu': DEFAULT_HAWAII,
  'America/Juneau': DEFAULT_ALASKA,
  'America/Anchorage': DEFAULT_ALASKA,
};

export const DEFAULT_LATLNG: LatLng = TIME_ZONE_TO_DEFAULT_LATLNG[
  activeTimezone
] || {
  lat: 41.001463,
  lng: -104.05337,
};

export const ZOOM_LEVELS = {
  STREET: 18,
  CITY: 9,
  REGION: 5,
  CONTINENT: 3,
};

const TIME_ZONE_TO_DEFAULT_ZOOM: Record<string, number> = {
  'Pacific/Honolulu': ZOOM_LEVELS.REGION,
  'America/Juneau': ZOOM_LEVELS.CONTINENT,
  'America/Anchorage': ZOOM_LEVELS.CONTINENT,
};

export const DEFAULT_ZOOM =
  TIME_ZONE_TO_DEFAULT_ZOOM[activeTimezone] || ZOOM_LEVELS.CONTINENT;

export const DEFAULT_VIEW_STATE: DefaultViewState = {
  latitude: DEFAULT_LATLNG.lat,
  longitude: DEFAULT_LATLNG.lng,
  zoom: DEFAULT_ZOOM,
  minZoom: ZOOM_LEVELS.CONTINENT,
  maxZoom: ZOOM_LEVELS.STREET,
  // Set zIndex to 0 to create a stacking context for markers. Markers get zIndexes
  // (sometimes very large ones) to sort them visually. This stacking context ensures
  // that none of the markers are displayed above other UI elements outside the map
  // view.
  style: { width: '100%', height: '100%', zIndex: 0 },
};

/**
 * Floor and ceiling for aircraft marker z-indexes. Aircraft use their altitude
 * in feet as their z-index (see `parseAltitudeToZIndex`), which is unbounded
 * vendor data, so it gets clamped to this range.
 *
 * The floor sits just above the geo event markers so that even a grounded
 * aircraft stays on top of them. The ceiling is set generously: too high only
 * costs stacking fidelity between aircraft that are both above it.
 */
export const MIN_AIRCRAFT_MARKER_Z_INDEX = GEO_EVENT_MARKER_Z_INDEX + 1;
export const MAX_AIRCRAFT_MARKER_Z_INDEX = 90000;

/**
 * Top of the marker stack: the search/place location pin (and its info box
 * label), the dropped map pin, and the user position dot. These orient the user
 * on the map, so they must never be covered by data markers.
 *
 * All markers share one stacking context (see `DEFAULT_VIEW_STATE.style`), so
 * this is derived from the aircraft ceiling (aircraft are the only markers with
 * dynamic z-indexes). Stays below the map UI: 99996-99998 measure tool, 99999
 * control buttons (`src/css/index.css`).
 */
export const TOP_MARKER_Z_INDEX = MAX_AIRCRAFT_MARKER_Z_INDEX + 1;

export enum MapViewDimension {
  '2D' = '2D',
  '3D' = '3D',
}

export const DEFAULT_3D_PITCH = 45;
export const DEFAULT_3D_EXAGGERATION = 1;

// These values are used both for display and persistance values, so do not change!
export const MapBaseLayerNames = {
  STREET: 'Street Map',
  SATELLITE: 'Satellite Map',
  TOPOGRAPHIC: 'Topographic Map',
} as const;

export const MapBaseLayerStyles = {
  [MapBaseLayerNames.STREET]: mapboxStreetStyle,
  [MapBaseLayerNames.SATELLITE]: mapboxSatelliteStyle,
  [MapBaseLayerNames.TOPOGRAPHIC]: mapboxTopoStyle,
} as const;

// Map layers that don't have clickable entities associated with routes
export const MapLayersWithoutEntityDrawer = {
  ACTIVE_FIRE_PERIMETERS: 'ActiveFirePerimetersLayer',
  HISTORICAL_FIRE_PERIMETERS: 'HistoricalFirePerimetersLayer',
  MODIS: 'ModisLayer',
  VIIRS: 'ViirsLayer',
  SMOKE_COVERAGE: 'SmokeCoverageLayer',
  SURFACE_WIND: 'Surface Wind',
  RADAR: 'Radar',
  FLIGHT_TRACKER: 'FlightTracker',
  RADIO_TOWERS: 'RadioTowers',
  POWER_OUTAGES: 'PowerOutages',
  EVACUATION_ZONES: 'EvacuationZones',
  ELECTRICAL_LINES: 'ElectricalLines',
  GAS_PIPELINES: 'GasPipelines',
  LANDSAT: 'LandsatLayer',
  NGFS_DETECTIONS: 'NgfsDetectionsLayer',
  EXTERNAL_GEO_EVENTS: 'ExternalGeoEvents',
  FEDERAL_STATE_LOCAL: 'FederalStateLocal',
  RESPONSIBILITY_AREAS: 'ResponsibilityAreas',
  ELECTRIC_RETAIL_SERVICE: 'ElectricRetailService',
  FEMA_FLOOD_HAZARD: 'FemaFloodHazard',
  REPORTS: 'Reports',
  PHOTO_MARKERS: 'PhotoMarkers',
};

export const MapLayers = {
  ...MapLayersWithoutEntityDrawer,
  PURPLE_AIR: 'PurpleAir',
  CAMERAS: 'Cameras',
  PRIVATE_LAND_OWNERSHIP: 'PrivateLandOwnership',
  WATER_LEVEL: 'WaterLevel',
  WEATHER_STATIONS: 'WeatherStations',
  LIGHTNING: 'Lightning',
  RED_FLAG_WARNINGS: 'RedFlagWarnings',
  FLOOD_WEATHER: 'FloodWeather',
  WEA_ALERTS: 'WeaAlerts',
  // Bucket id for org/team external feature layers — used in isLayerVisible for team layer
  TEAM_LAYER: 'TeamLayer',
  TEAM_POLYGON_LAYER: 'TeamPolygonLayer',
  PLACES: 'PlacesLayer',
} as const;

// Values blend GeoEventTypes and MapLayers for non-GeoEvent Router Entities
export const ROUTER_ENTITY_TO_MAP_LAYER_DESIGNATION: Record<
  RouterEntityDrawer,
  string | string[]
> = {
  wildfire: GeoEventTypes.wildfire,
  location: GeoEventTypes.location,
  flooding: GeoEventTypes.flooding,
  hazard: GeoEventTypes.hazard,
  camera: MapLayers.CAMERAS,
  gauge: MapLayers.WATER_LEVEL,
  weatherStation: MapLayers.WEATHER_STATIONS,
  aqi: MapLayers.PURPLE_AIR,
  alert: [MapLayers.RED_FLAG_WARNINGS, MapLayers.FLOOD_WEATHER],
  place: [MapLayers.PLACES],
};

export const MapLayerAttributions = {
  [MapLayers.ACTIVE_FIRE_PERIMETERS]: 'NIFC, FIRIS',
  [MapLayers.HISTORICAL_FIRE_PERIMETERS]: 'CalFire',
  [MapLayers.MODIS]: 'NASA',
  [MapLayers.VIIRS]: 'NASA',
  [MapLayers.LANDSAT]: 'NASA',
  [MapLayers.NGFS_DETECTIONS]: 'NOAA',
  [MapLayers.SMOKE_COVERAGE]: 'NOAA',
  [MapLayers.LIGHTNING]: 'AEM',
  [MapLayers.PURPLE_AIR]:
    '<a href="https://www2.purpleair.com/?ref=esYmAhN1F4yWLv">PurpleAir</a>',
  [MapLayers.SURFACE_WIND]:
    'NOAA via <a href="https://weatherlayers.com/">WeatherLayers</a>',
  [MapLayers.RADAR]: 'NOAA MRMS',
  [MapLayers.RADIO_TOWERS]: 'CalFire',
  [MapLayers.FLIGHT_TRACKER]:
    '<a href="https://www.adsbexchange.com/">ADS-B Exchange</a>',
  [MapLayers.POWER_OUTAGES]: 'PowerOutageUS',
} as const;

export const AllMapLayers: MapLayer[] = Object.values(MapLayers);

export const MapLayersDetails: Record<
  Exclude<MapLayer, 'SmokeCoverageLayer'>,
  MapLayerDetail
> = {
  [MapLayers.ACTIVE_FIRE_PERIMETERS]: {
    titleKey: 'map.layers.activeFirePerimeters.title',
    subtitleKey: 'map.layers.activeFirePerimeters.subtitle',
    requiresWebGL2: false,
    imageSrc: '/images/fire-perimeters-active-thumb.png',
    keyboardShortcut: 'p',
  },
  [MapLayers.HISTORICAL_FIRE_PERIMETERS]: {
    titleKey: 'map.layers.historicalFirePerimeters.title',
    subtitleKey: 'map.layers.historicalFirePerimeters.subtitle',
    requiresWebGL2: false,
    imageSrc: '/images/fire-perimeters-historical-thumb.png',
    keyboardShortcut: 'h',
  },
  [MapLayers.VIIRS]: {
    titleKey: 'map.layers.viirs.title',
    subtitleKey: 'map.layers.viirs.subtitle',
    requiresWebGL2: false,
    imageSrc: '/images/satellite-hotspots-viirs-thumb.png',
    keyboardShortcut: 'v',
  },
  [MapLayers.MODIS]: {
    titleKey: 'map.layers.modis.title',
    subtitleKey: 'map.layers.modis.subtitle',
    requiresWebGL2: false,
    imageSrc: '/images/satellite-hotspots-modis-thumb.png',
    keyboardShortcut: 'v',
  },
  [MapLayers.LANDSAT]: {
    titleKey: 'map.layers.landsat.title',
    subtitleKey: 'map.layers.landsat.subtitle',
    requiresWebGL2: false,
    imageSrc: '/images/satellite-hotspots-landsat-thumb.png',
    keyboardShortcut: 'v',
  },
  [MapLayers.NGFS_DETECTIONS]: {
    titleKey: 'map.layers.ngfsDetections.title',
    subtitleKey: 'map.layers.ngfsDetections.subtitle',
    requiresWebGL2: false,
    experimental: true,
    imageSrc: '/images/satellite-hotspots-ngfs-thumb.png',
    keyboardShortcut: 'v',
  },
  [MapLayers.SURFACE_WIND]: {
    titleKey: 'map.layers.surfaceWind.title',
    subtitleKey: 'map.layers.surfaceWind.subtitle',
    requiresWebGL2: true,
    keyboardShortcut: 'w',
  },
  [MapLayers.RADAR]: {
    titleKey: 'map.layers.radar.title',
    subtitleKey: 'map.layers.radar.subtitle',
    requiresWebGL2: true,
  },
  [MapLayers.PURPLE_AIR]: {
    titleKey: 'map.layers.purpleAir.title',
    subtitleKey: 'map.layers.purpleAir.subtitle',
    requiresWebGL2: false,
    keyboardShortcut: 'a',
  },
  [MapLayers.LIGHTNING]: {
    titleKey: 'map.layers.lightning.title',
    subtitleKey: 'map.layers.lightning.subtitle',
    requiresWebGL2: false,
    keyboardShortcut: 'i',
    timelineLayerId: 'lightning',
  },
  [MapLayers.FLIGHT_TRACKER]: {
    titleKey: 'map.layers.flightTracker.title',
    subtitleKey: 'map.layers.flightTracker.subtitle',
    requiresWebGL2: false,
    minMembership: 'regular',
    requiredPermissions: ['canViewFlightTrackerLayer'],
    keyboardShortcut: 'f',
  },
  [MapLayers.RADIO_TOWERS]: {
    titleKey: 'map.layers.radioTowers.title',
    subtitleKey: 'map.layers.radioTowers.subtitle',
    requiresWebGL2: false,
    imageSrc: '/images/radio-repeaters-thumb.png',
    minMembership: 'pro',
    keyboardShortcut: 't',
  },
  [MapLayers.RED_FLAG_WARNINGS]: {
    titleKey: 'map.layers.redFlagWarnings.title',
    subtitleKey: 'map.layers.redFlagWarnings.subtitle',
    requiresWebGL2: false,
    keyboardShortcut: 'r',
  },
  [MapLayers.FLOOD_WEATHER]: {
    titleKey: 'map.layers.floodWeather.title',
    subtitleKey: 'map.layers.floodWeather.subtitle',
    requiresWebGL2: false,
  },
  [MapLayers.POWER_OUTAGES]: {
    titleKey: 'map.layers.powerOutages.title',
    subtitleKey: 'map.layers.powerOutages.subtitle',
    requiresWebGL2: false,
    keyboardShortcut: 'b',
  },
  [MapLayers.CAMERAS]: {
    titleKey: 'map.layers.cameras.title',
    subtitleKey: 'map.layers.cameras.subtitle',
    requiresWebGL2: false,
    keyboardShortcut: 'c',
  },
  [MapLayers.EVACUATION_ZONES]: {
    titleKey: 'map.layers.evacuationZones.title',
    subtitleKey: 'map.layers.evacuationZones.subtitle',
    requiresWebGL2: false,
    minMembership: 'pro',
    keyboardShortcut: 'z',
  },
  [MapLayers.ELECTRICAL_LINES]: {
    titleKey: 'map.layers.electricalLines.title',
    subtitleKey: 'map.layers.electricalLines.subtitle',
    requiresWebGL2: false,
    imageSrc: '/images/electrical-lines-active-thumb.png',
    minMembership: 'pro',
  },
  [MapLayers.GAS_PIPELINES]: {
    titleKey: 'map.layers.gasPipelines.title',
    subtitleKey: 'map.layers.gasPipelines.subtitle',
    requiresWebGL2: false,
    imageSrc: '/images/gas-pipelines-active-thumb.png',
    minMembership: 'pro',
  },
  [MapLayers.EXTERNAL_GEO_EVENTS]: {
    titleKey: 'map.layers.externalGeoEvents.title',
    subtitleKey: 'map.layers.externalGeoEvents.subtitle',
    requiresWebGL2: false,
    internalOnly: true,
    keyboardShortcut: 'e',
  },
  [MapLayers.FEDERAL_STATE_LOCAL]: {
    titleKey: 'map.layers.federalStateLocal.title',
    subtitleKey: 'map.layers.federalStateLocal.subtitle',
    requiresWebGL2: false,
    minMembership: 'pro',
    imageSrc: '/images/federal-land-ownership-thumb.png',
    keyboardShortcut: 'o f',
  },
  [MapLayers.RESPONSIBILITY_AREAS]: {
    titleKey: 'map.layers.responsibilityAreas.title',
    subtitleKey: 'map.layers.responsibilityAreas.subtitle',
    requiresWebGL2: false,
    minMembership: 'pro',
    imageSrc: '/images/ca-responsibility-thumb.png',
    keyboardShortcut: 'o r',
  },
  [MapLayers.ELECTRIC_RETAIL_SERVICE]: {
    titleKey: 'map.layers.electricRetailService.title',
    subtitleKey: 'map.layers.electricRetailService.subtitle',
    requiresWebGL2: false,
    minMembership: 'pro',
    imageSrc: '/images/electric-retail-territory-thumb.png',
    keyboardShortcut: 'o e',
  },
  [MapLayers.PRIVATE_LAND_OWNERSHIP]: {
    titleKey: 'map.layers.privateLandOwnership.title',
    subtitleKey: 'map.layers.privateLandOwnership.subtitle',
    requiresWebGL2: false,
    imageSrc: '/images/private-land-ownership-thumb.png',
    minMembership: 'pro',
    keyboardShortcut: 'o p',
  },
  [MapLayers.WATER_LEVEL]: {
    titleKey: 'map.layers.waterLevel.title',
    subtitleKey: 'map.layers.waterLevel.subtitle',
    requiresWebGL2: false,
    keyboardShortcut: 'g',
  },
  [MapLayers.WEATHER_STATIONS]: {
    titleKey: 'map.layers.weatherStations.title',
    subtitleKey: 'map.layers.weatherStations.subtitle',
    requiresWebGL2: false,
    keyboardShortcut: 's',
  },
  [MapLayers.FEMA_FLOOD_HAZARD]: {
    titleKey: 'map.layers.femaFloodHazard.title',
    subtitleKey: 'map.layers.femaFloodHazard.subtitle',
    requiresWebGL2: false,
  },
} as const;

export const TIMELINE_LAYER_IDS = ['radar', 'smoke', 'lightning'] as const;
export type TimelineLayerId = (typeof TIMELINE_LAYER_IDS)[number];
export const DEFAULT_TIMELINE_LAYER_ID: TimelineLayerId = 'radar';

export type MapTimelineLayerDetail = {
  titleKey: string;
  subtitleKey: string;
  shortSubtitleKey: string;
  attribution: { href: string; label: string };
  markIntervalMinutes: number;
  /** See `AnimatedTimelineControlProps` */
  markLabelIntervalMinutes?: number;
  emphasizeEndLabel?: boolean;
  timelineLabelKeys: { start: string; end: string };
  /** Bound as `l <key>`; layers without one are command-palette-only */
  keyboardShortcut?: string;
};

export type MapTimelineLayer = MapTimelineLayerDetail & { id: TimelineLayerId };

export const MapTimelineLayersDetails: Record<
  TimelineLayerId,
  MapTimelineLayerDetail
> = {
  radar: {
    titleKey: 'map.timelineLayers.radar.title',
    subtitleKey: 'map.timelineLayers.radar.subtitle',
    shortSubtitleKey: 'map.timelineLayers.radar.shortSubtitle',
    keyboardShortcut: 'd',
    attribution: {
      href: 'https://www.wetdogweather.com',
      label: 'Wet Dog Weather',
    },
    markIntervalMinutes: 30,
    timelineLabelKeys: {
      start: 'map.timelineLayers.radar.timeline.start',
      end: 'map.timelineLayers.radar.timeline.end',
    },
  },
  smoke: {
    titleKey: 'map.timelineLayers.smoke.title',
    subtitleKey: 'map.timelineLayers.smoke.subtitle',
    shortSubtitleKey: 'map.timelineLayers.smoke.shortSubtitle',
    attribution: { href: 'https://opensnow.com', label: 'OpenSnow' },
    markIntervalMinutes: 360,
    timelineLabelKeys: {
      start: 'map.timelineLayers.smoke.timeline.start',
      end: 'map.timelineLayers.smoke.timeline.end',
    },
  },
  lightning: {
    titleKey: 'map.timelineLayers.lightning.title',
    subtitleKey: 'map.timelineLayers.lightning.subtitle',
    shortSubtitleKey: 'map.timelineLayers.lightning.shortSubtitle',
    attribution: { href: 'https://aem.eco', label: 'AEM' },
    markIntervalMinutes: 720,
    markLabelIntervalMinutes: 1440,
    emphasizeEndLabel: true,
    timelineLabelKeys: {
      start: 'map.timelineLayers.lightning.timeline.start',
      end: 'map.timelineLayers.lightning.timeline.end',
    },
  },
};

export const PlaceMapMarkerLabelColor = '#1F1F1F';

export const FILL_PATTERN_LAYER_MIN_ZOOM = 10;

export const MOBILE_LAYER_DRAWER_HEIGHT = 610;
export const MOBILE_LAYER_DRAWER_HEIGHT_USER = 670;

export const LAYERS_DRAWER_WIDTH = 375;

export const LAYER_GROUPS = {
  firePerimeters: [
    MapLayers.ACTIVE_FIRE_PERIMETERS,
    MapLayers.HISTORICAL_FIRE_PERIMETERS,
  ],
  satelliteHotspots: [
    MapLayers.NGFS_DETECTIONS,
    MapLayers.VIIRS,
    MapLayers.MODIS,
    MapLayers.LANDSAT,
  ],
  professional: [MapLayers.EXTERNAL_GEO_EVENTS],
  federalLands: [
    MapLayers.PRIVATE_LAND_OWNERSHIP,
    MapLayers.FEDERAL_STATE_LOCAL,
    MapLayers.RESPONSIBILITY_AREAS,
    MapLayers.ELECTRIC_RETAIL_SERVICE,
  ],
  criticalInfrastructure: [
    MapLayers.ELECTRICAL_LINES,
    MapLayers.GAS_PIPELINES,
    MapLayers.RADIO_TOWERS,
  ],
} as const;

// Satellite hotspot layers shown when the
// show_heat_detections_from_gis_in_frontend flag is off.
export const UNGATED_HOTSPOT_LAYERS: readonly MapLayer[] = [
  MapLayers.VIIRS,
  MapLayers.MODIS,
];

// Satellite identifiers exactly as they appear in the `satellite` property of
// heat-detection tiles (see the heat_detections_gis_24h view in
// watchduty-backend).
export const SATELLITE_NAMES = {
  NOAA_21: 'NOAA-21',
  SUOMI_NPP: 'Suomi-NPP',
  NOAA_20: 'NOAA-20',
  MODIS: 'MODIS',
  LANDSAT: 'Landsat',
} as const;

export type SatelliteName =
  (typeof SATELLITE_NAMES)[keyof typeof SATELLITE_NAMES];

// Satellite names that each hotspot layer surfaces.
export const SATELLITES_BY_LAYER = {
  [MapLayers.VIIRS]: [
    SATELLITE_NAMES.NOAA_21,
    SATELLITE_NAMES.SUOMI_NPP,
    SATELLITE_NAMES.NOAA_20,
  ],
  [MapLayers.MODIS]: [SATELLITE_NAMES.MODIS],
  [MapLayers.LANDSAT]: [SATELLITE_NAMES.LANDSAT],
} as const;

export const MAIN_REFETCH_INTERVAL_MS = 1000 * 60 * 2; // 2 minutes

export const LOCATION_ZOOM_LEVEL = 13; // ~2000 ft

export const EXTERNAL_GEO_EVENT_ZOOM_LEVEL = 9;

export const POWER_OUTAGE_LAYERS_STALE_TIME_MS = 1000 * 60 * 5; // 5 minutes

export const OVERLAPPING_FEATURES_FOOTER_DESKTOP_MIN_HEIGHT_PX = 60;
